import React            from 'react';
import View             from '@widesk-ui/components/View';
import Button           from '@widesk-ui/components/Button';
import Select           from '@widesk-ui/components/form/Select';
import { EyeOutlined }          from '@ant-design/icons';
import { EyeInvisibleOutlined } from '@ant-design/icons';
import { DECOR_STYLE_LABELS }  from '@/TokamapEditor/helpers/planDecor';
import { decorCandidates }     from '@/TokamapEditor/helpers/planDecor';
import type { PlanDecor }      from '@/TokamapEditor/helpers/planDecor';
import { ROLE_LABELS }         from '@/TokamapEditor/helpers/planLayerMapping';
import type { PlanCategory }   from '@/TokamapEditor/helpers/planLayerMapping';
import type { DxfLayer }       from '@models/map/dxfTypes';
import type { PlanDecorStyle } from '@models/map/dxfTypes';

type Props = {
	layers: DxfLayer[];
	/** Les lignes telles qu'elles partiront : un calque pris par une ligne cochée n'est pas proposé. */
	categories: PlanCategory[];
	decor: PlanDecor;
	previewedLayer: string | null;
	onChange: (layer: string, style: PlanDecorStyle | null) => void;
	onPreviewLayer: (layer: string) => void;
};

const STYLE_OPTIONS = [
	{ value: '', label: 'Pas du décor' },
	...Object.entries(DECOR_STYLE_LABELS).map(([value, label]) => ({ value, label })),
];

const PlanDecorLayers = ({ layers, categories, decor, previewedLayer, onChange, onPreviewLayer }: Props) => {
	const candidates = React.useMemo(
		() => decorCandidates(layers, categories, decor),
		[layers, categories, decor],
	);

	if (!candidates.length) {
		return <View size={12} color="#999">Aucun calque nommé à désigner en décor.</View>;
	}

	return (
		<View gap={8}>
			<View size={11} color="#999">
				Le décor est dessiné en fond de plan, sous les POI, et ne crée rien. Votre choix sera
				proposé aux prochains imports du projet.
			</View>

			{candidates.map(layer => (
				<View key={layer.name} row gap={8} center style={{ borderTop: '1px solid #f0f0f0', paddingTop: 8 }}>
					<View flex>
						<View size={13}>{layer.name}</View>
						<View size={11} color="#999">{ROLE_LABELS[layer.role]}, {layer.entities} entité(s)</View>
					</View>
					<View style={{ width: 140 }}>
						<Select
							options={STYLE_OPTIONS}
							value={decor[layer.name] ?? ''}
							onChange={(value: unknown) => onChange(layer.name, (String(value ?? '') || null) as PlanDecorStyle | null)}
						/>
					</View>
					<Button
						type="text"
						size="small"
						title={previewedLayer === layer.name ? 'Ne plus surligner' : 'Surligner ce calque sur la carte'}
						icon={previewedLayer === layer.name ? <EyeInvisibleOutlined /> : <EyeOutlined />}
						onClick={() => onPreviewLayer(layer.name)}
					/>
				</View>
			))}
		</View>
	);
};

export default PlanDecorLayers;
